import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom, Observable, tap } from 'rxjs';
import { TripImpact } from '../models/trip-impact';
import { environment } from '../../../environments/environment';
import {
  CustomPieChartData,
  ImpactReportData,
} from '../models/impactReportData';
import { TranslocoService } from '@jsverse/transloco';
import { ChartData } from 'chart.js';
import { ColorsService } from './colors.service';
import { Trip } from 'src/app/trip-calculator/models/trip';
import { Country } from 'src/app/trip-calculator/models/country';
import { DetailedImpactReport } from '../models/DetailedImpactReport';
import { TripReductionTipsResponse } from '../models/reduction-tip';
import { ViewMode } from '../utils/impact-calculator';
import {
  BOAT_TYPES,
  BUS_TYPES,
  CAR_TYPES,
  PLANE_TYPES,
  TRAIN_TYPES,
  Transport,
  TransportCategory,
  TransportMode,
  TWO_WHEELS_TYPES,
} from 'src/app/trip-calculator/models/transport';

export type PieChartType =
  | 'overview'
  | 'transport'
  | 'accommodation'
  | 'activity'
  | 'food'
  | 'waste';

@Injectable({
  providedIn: 'root',
})
export class ImpactService {
  private http = inject(HttpClient);
  private translocoService = inject(TranslocoService);
  private colorsService = inject(ColorsService);

  transportDistanceData = signal<{ type: string; km: number } | undefined>(
    undefined,
  );
  impactReportData = signal<ImpactReportData | undefined>(undefined);

  /**
   * Last report loaded by `getDetailedImpactReport()`. Public so the page can
   * react to a reload it did not ask for — applying reduction tips rewrites
   * the trip and reads the report back.
   */
  readonly detailedReport = signal<DetailedImpactReport | null>(null);

  /** Trip of the loaded report — `null` before the first load. */
  readonly trip = computed(() => this.detailedReport()?.trip ?? null);

  /**
   * `getOverviewDetailedBreakdown()` applied to the loaded trip, as a signal.
   * `null` while no trip is loaded or its impact has not been calculated yet.
   */
  readonly overview = computed(() => {
    const trip = this.trip();
    if (!trip?.tripImpact) return null;
    return this.getOverviewDetailedBreakdown(trip);
  });

  /**
   * Group vs per-traveller reading. Shared by every component of the impact
   * page, so the toggles of the overview and of the stats section stay in sync.
   */
  readonly viewMode = signal<ViewMode>('group');

  /** `true` when every figure must be displayed per traveller. */
  readonly perPerson = computed(() => this.viewMode() === 'individual');

  /**
   *
   * @param id id of the trip
   * @returns Observables with Trips with impact
   * @deprecated use getDetailedImpactReport() instead
   */
  getTripImpact(id: string): Observable<{ trip: Trip; country: Country }> {
    return this.http.get<{
      trip: Trip;
      country: Country;
    }>(environment.apiUrl + '/impact-reports/' + id);
  }

  getDetailedImpactReport(id: string): Observable<DetailedImpactReport> {
    return this.http
      .get<DetailedImpactReport>(
        environment.apiUrl + '/impact-reports/' + id + '/detailed',
      )
      .pipe(tap((report) => this.detailedReport.set(report)));
  }
  /**
   *
   * @param id
   * @deprecated use get detailedImpactReport instead
   */
  getImpactReportData(id: string) {
    this.getTripImpact(id).subscribe({
      next: async (response: { trip: Trip; country: Country }) => {
        this.impactReportData.set(await this.transformData(response));
      },
      error: (err) => {
        console.error('error during data traitment : ', err);
      },
    });
  }

  /** Breakdown category of a transport type — also used to map tips to slices. */
  getTransportCategory(subtype: string): TransportCategory | undefined {
    const transportCategoryMap: {
      types: readonly string[];
      category: TransportCategory;
    }[] = [
      { types: PLANE_TYPES, category: TransportCategory.Plane },
      { types: TRAIN_TYPES, category: TransportCategory.Train },
      { types: CAR_TYPES, category: TransportCategory.Car },
      { types: TWO_WHEELS_TYPES, category: TransportCategory.TwoWheels },
      { types: BUS_TYPES, category: TransportCategory.Bus },
      { types: BOAT_TYPES, category: TransportCategory.Boat },
    ];

    for (const { types, category } of transportCategoryMap) {
      if (types.includes(subtype as TransportMode)) {
        return category;
      }
    }
    return undefined;
  }

  // Option 1: Catégories détaillées (tous les types de transport séparés)
  getOverviewDetailed(trip: Trip): CustomPieChartData {
    const { totalImpact, detail } = this.getOverviewDetailedBreakdown(trip);
    return this.buildCustomPieChartData(totalImpact, detail, 'overview');
  }

  /**
   * Same breakdown as `getOverviewDetailed()`, before it is turned into chart
   * data: transports split by category (plane, car, train…), then the other
   * categories. Use this when you need the raw values rather than a pie chart.
   */
  getOverviewDetailedBreakdown(trip: Trip): {
    totalImpact: number;
    detail: { type: string; impact: number }[];
  } {
    // Regroupe chaque transport par catégorie (Avion, Voiture, etc.)
    const detail: { type: string; impact: number }[] = [];
    const transportCategories: { [type: string]: number } = {};
    trip.transports?.forEach((transport) => {
      const category = this.getTransportCategory(
        this.getTransportSubtypeOrType(transport),
      );
      if (category && transport.transportImpactDetail?.impact) {
        transportCategories[category] =
          (transportCategories[category] || 0) +
          transport.transportImpactDetail.impact;
      }
    });

    Object.entries(transportCategories).forEach(([type, impact]) => {
      detail.push({ type, impact });
    });

    trip.tripImpact!.accommodationImpactDetail.length > 0 &&
      detail.push({
        type: 'accommodationImpact',
        impact: trip.tripImpact!.accommodationImpact,
      });
    trip.tripImpact!.activityImpactDetail.length > 0 &&
      detail.push({
        type: 'activityImpact',
        impact: trip.tripImpact!.activityImpact,
      });
    trip.tripImpact!.foodImpactDetail.length > 0 &&
      detail.push({ type: 'foodImpact', impact: trip.tripImpact!.foodImpact });
    trip.tripImpact!.wasteImpact > 0 &&
      detail.push({
        type: 'wasteImpact',
        impact: trip.tripImpact!.wasteImpact,
      });

    const totalImpact = detail.reduce((sum, item) => sum + item.impact, 0);

    return { totalImpact, detail };
  }

  getOverviewWithoutMainTransport(
    trip: Trip,
    mode: 'detailed' | 'mainVsOnSite',
  ): CustomPieChartData {
    if (mode === 'detailed') {
      // Comme getOverviewDetailed mais sans les transports principaux
      const detail: { type: string; impact: number }[] = [];
      const transportCategories: { [type: string]: number } = {};

      trip.transports?.forEach((transport) => {
        if (transport.isMainTransport) return;
        const category = this.getTransportCategory(
          this.getTransportSubtypeOrType(transport),
        );
        if (category && transport.transportImpactDetail?.impact) {
          transportCategories[category] =
            (transportCategories[category] || 0) +
            transport.transportImpactDetail.impact;
        }
      });

      Object.entries(transportCategories).forEach(([type, impact]) => {
        detail.push({ type, impact });
      });

      trip.tripImpact!.accommodationImpactDetail.length > 0 &&
        detail.push({
          type: 'accommodationImpact',
          impact: trip.tripImpact!.accommodationImpact,
        });
      trip.tripImpact!.activityImpactDetail.length > 0 &&
        detail.push({
          type: 'activityImpact',
          impact: trip.tripImpact!.activityImpact,
        });
      trip.tripImpact!.foodImpactDetail.length > 0 &&
        detail.push({
          type: 'foodImpact',
          impact: trip.tripImpact!.foodImpact,
        });
      trip.tripImpact!.wasteImpact > 0 &&
        detail.push({
          type: 'wasteImpact',
          impact: trip.tripImpact!.wasteImpact,
        });

      const totalImpact = detail.reduce((sum, item) => sum + item.impact, 0);

      return this.buildCustomPieChartData(totalImpact, detail, 'overview');
    } else {
      // Comme mainVsOnSite mais sans transport principal
      const overview = this.getOverview(trip);
      const filteredDetail = overview.detail.filter(
        (item) => item.type !== 'mainTransportImpact',
      );
      const totalImpact = filteredDetail.reduce(
        (sum, item) => sum + item.impact,
        0,
      );
      return this.buildCustomPieChartData(
        totalImpact,
        filteredDetail,
        'overview',
      );
    }
  }

  getOverviewPerTravellerDetailed(trip: Trip): CustomPieChartData {
    const detail: { type: string; impact: number }[] = [];
    const transportCategories: { [type: string]: number } = {};

    trip.transports?.forEach((transport) => {
      const category = this.getTransportCategory(
        this.getTransportSubtypeOrType(transport),
      );
      if (category && transport.transportImpactDetail?.impact) {
        transportCategories[category] =
          (transportCategories[category] || 0) +
          transport.transportImpactDetail.impact / trip.travellers!;
      }
    });

    Object.entries(transportCategories).forEach(([type, impact]) => {
      detail.push({ type, impact });
    });

    trip.tripImpact!.accommodationImpactDetail.length > 0 &&
      detail.push({
        type: 'accommodationPerTraveller',
        impact: trip.tripImpact!.accommodationPerTraveller,
      });
    trip.tripImpact!.activityImpactDetail.length > 0 &&
      detail.push({
        type: 'activityPerTraveller',
        impact: trip.tripImpact!.activityPerTraveller,
      });
    trip.tripImpact!.foodImpactDetail.length > 0 &&
      detail.push({
        type: 'foodPerTraveller',
        impact: trip.tripImpact!.foodPerTraveller,
      });
    trip.tripImpact!.wastePerTraveller > 0 &&
      detail.push({
        type: 'wastePerTraveller',
        impact: trip.tripImpact!.wastePerTraveller,
      });

    const totalImpact = detail.reduce((sum, item) => sum + item.impact, 0);

    return this.buildCustomPieChartData(totalImpact, detail, 'overview');
  }

  getOverviewPerTravellerWithoutMainTransport(
    trip: Trip,
    mode: 'detailed' | 'mainVsOnSite',
  ): CustomPieChartData {
    if (mode === 'detailed') {
      const detail: { type: string; impact: number }[] = [];
      const transportCategories: { [type: string]: number } = {};

      trip.transports?.forEach((transport) => {
        if (transport.isMainTransport) return;
        const category = this.getTransportCategory(
          this.getTransportSubtypeOrType(transport),
        );
        if (category && transport.transportImpactDetail?.impact) {
          transportCategories[category] =
            (transportCategories[category] || 0) +
            transport.transportImpactDetail.impact / trip.travellers!;
        }
      });

      Object.entries(transportCategories).forEach(([type, impact]) => {
        detail.push({ type, impact });
      });

      trip.tripImpact!.accommodationImpactDetail.length > 0 &&
        detail.push({
          type: 'accommodationPerTraveller',
          impact: trip.tripImpact!.accommodationPerTraveller,
        });
      trip.tripImpact!.activityImpactDetail.length > 0 &&
        detail.push({
          type: 'activityPerTraveller',
          impact: trip.tripImpact!.activityPerTraveller,
        });
      trip.tripImpact!.foodImpactDetail.length > 0 &&
        detail.push({
          type: 'foodPerTraveller',
          impact: trip.tripImpact!.foodPerTraveller,
        });
      trip.tripImpact!.wastePerTraveller > 0 &&
        detail.push({
          type: 'wastePerTraveller',
          impact: trip.tripImpact!.wastePerTraveller,
        });

      const totalImpact = detail.reduce((sum, item) => sum + item.impact, 0);

      return this.buildCustomPieChartData(totalImpact, detail, 'overview');
    } else {
      const overviewPerTraveller = this.getOverviewPerTraveller(trip);
      const filteredDetail = overviewPerTraveller.detail.filter(
        (item) => item.type !== 'mainTransportImpact',
      );
      const totalImpact = filteredDetail.reduce(
        (sum, item) => sum + item.impact,
        0,
      );
      return this.buildCustomPieChartData(
        totalImpact,
        filteredDetail,
        'overview',
      );
    }
  }

  getOverviewWithoutTransport(trip: Trip, mode: 'detailed' | 'mainVsOnSite') {
    if (mode === 'detailed') {
      const detail: { type: string; impact: number }[] = [];
      const transportCategories: { [type: string]: number } = {};

      trip.transports?.forEach((transport) => {
        if (transport.isMainTransport) return;
        const category = this.getTransportCategory(
          this.getTransportSubtypeOrType(transport),
        );
        if (category && transport.transportImpactDetail?.impact) {
          transportCategories[category] =
            (transportCategories[category] || 0) +
            transport.transportImpactDetail.impact;
        }
      });

      Object.entries(transportCategories).forEach(([type, impact]) => {
        detail.push({ type, impact });
      });

      trip.tripImpact!.accommodationImpactDetail.length > 0 &&
        detail.push({
          type: 'accommodationImpact',
          impact: trip.tripImpact!.accommodationImpact,
        });
      trip.tripImpact!.activityImpactDetail.length > 0 &&
        detail.push({
          type: 'activityImpact',
          impact: trip.tripImpact!.activityImpact,
        });
      trip.tripImpact!.foodImpactDetail.length > 0 &&
        detail.push({
          type: 'foodImpact',
          impact: trip.tripImpact!.foodImpact,
        });
      trip.tripImpact!.wasteImpact > 0 &&
        detail.push({
          type: 'wasteImpact',
          impact: trip.tripImpact!.wasteImpact,
        });

      const totalImpact = detail.reduce((sum, item) => sum + item.impact, 0);

      return {
        totalImpact,
        detail: detail,
      };
    } else {
      // Comme mainVsOnSite mais sans transport principal
      const overview = this.getOverview(trip);
      const filteredDetail = overview.detail.filter(
        (item) => item.type !== 'mainTransportImpact',
      );
      const totalImpact = filteredDetail.reduce(
        (sum, item) => sum + item.impact,
        0,
      );
      return {
        totalImpact,
        detail: filteredDetail,
      };
    }
  }

  async transformData(rawData: {
    trip: Trip;
    country: Country;
  }): Promise<ImpactReportData> {
    // console.log(rawData.trip.tripImpact);
    const overview = this.getOverview(rawData.trip);
    const overviewWithoutTransport = this.getOverviewWithoutTransport(
      rawData.trip,
      'mainVsOnSite',
    );
    const overviewPerTraveller = this.getOverviewPerTraveller(rawData.trip);
    const groupedTransport = this.groupTransportSubCategoriesByType(
      rawData.trip.transports || [],
    );

    const refinedData: ImpactReportData = {
      trip: rawData.trip,
      overview: this.buildCustomPieChartData(
        overview.totalImpact,
        overview.detail,
        'overview',
      ),
      overviewWithoutTransport: this.buildCustomPieChartData(
        overviewWithoutTransport.totalImpact,
        overviewWithoutTransport.detail,
        'overview',
      ),
      overviewPerTraveller: this.buildCustomPieChartData(
        overviewPerTraveller.totalImpact,
        overviewPerTraveller.detail,
        'overview',
      ),
      transportDetail: this.buildCustomPieChartData(
        rawData.trip.tripImpact!.transportImpact,
        groupedTransport.map(({ type, impact }) => ({ type, impact })),
        'transport',
      ),
      accommodationDetail: this.buildCustomPieChartData(
        rawData.trip.tripImpact!.accommodationImpact,
        rawData.trip.tripImpact!.accommodationImpactDetail,
        'accommodation',
      ),
      activityDetail: this.buildCustomPieChartData(
        rawData.trip.tripImpact!.activityImpact,
        rawData.trip.tripImpact!.activityImpactDetail,
        'activity',
      ),
      foodDetail: this.buildCustomPieChartData(
        rawData.trip.tripImpact!.foodImpact,
        rawData.trip.tripImpact!.foodImpactDetail,
        'food',
      ),
      carbonIntensity: this.round(rawData.trip.tripImpact!.carbonIntensity),
      annualBudget: this.round(
        rawData.trip.tripImpact!.percentageOfAnnualBudget,
      ),
      countryName: this.getCountryByLang(rawData.country),
      equivalentCards: this.getEquivalentCards(rawData.trip.tripImpact!),
      impactPerDay: await this.buildImpactPerDayChartData(rawData.trip),
    };
    return refinedData;
  }

  getTransportTypeKey(type: string | string[] | undefined): string {
    if (Array.isArray(type)) {
      return type[0] ?? '';
    }
    return type ?? '';
  }

  private groupTransportSubCategoriesByType(
    transports: Transport[],
  ): { type: string; impact: number; km: number }[] {
    const grouped: {
      [type: string]: { type: string; impact: number; km: number };
    } = {};

    transports.forEach((t) => {
      const type = this.getTransportTypeKey(t.type);
      if (!grouped[type]) {
        grouped[type] = {
          type,
          impact: 0,
          km: 0,
        };
      }
      grouped[type].impact += t.transportImpactDetail?.impact ?? 0;
    });

    return Object.values(grouped);
  }

  getOverview(trip: Trip) {
    const detail: {
      type: string;
      impact: number;
      subcategories?: { type: string; impact: number }[];
    }[] = [];

    if (trip.tripImpact!.transportImpactDetail.length > 0) {
      let mainTransportImpact = 0;
      let onSiteTransportImpact = 0;
      const onSiteTransports: { type: string; impact: number }[] = [];

      trip.transports?.forEach((transport) => {
        if (transport.isMainTransport) {
          mainTransportImpact += transport.transportImpactDetail?.impact ?? 0;
        } else if (transport.transportImpactDetail?.impact) {
          onSiteTransportImpact += transport.transportImpactDetail.impact;

          const category = this.getTransportCategory(
            this.getTransportSubtypeOrType(transport),
          );
          if (category) {
            onSiteTransports.push({
              type: category,
              impact: transport.transportImpactDetail.impact,
            });
          }
        }
      });

      const groupedOnSite: { [type: string]: number } = {};
      onSiteTransports.forEach((item) => {
        groupedOnSite[item.type] =
          (groupedOnSite[item.type] || 0) + item.impact;
      });
      const groupedOnSiteArray = Object.entries(groupedOnSite).map(
        ([type, impact]) => ({
          type,
          impact,
        }),
      );

      mainTransportImpact > 0 &&
        detail.push({
          type: 'mainTransportImpact',
          impact: mainTransportImpact,
        });

      onSiteTransportImpact > 0 &&
        detail.push({
          type: 'onSiteTransportImpact',
          impact: onSiteTransportImpact,
          subcategories: groupedOnSiteArray,
        });
    }

    trip.tripImpact!.accommodationImpactDetail.length > 0 &&
      detail.push({
        type: 'accommodationImpact',
        impact: trip.tripImpact!.accommodationImpact,
      });
    trip.tripImpact!.activityImpactDetail.length > 0 &&
      detail.push({
        type: 'activityImpact',
        impact: trip.tripImpact!.activityImpact,
      });
    trip.tripImpact!.foodImpactDetail.length > 0 &&
      detail.push({ type: 'foodImpact', impact: trip.tripImpact!.foodImpact });
    trip.tripImpact!.wasteImpact > 0 &&
      detail.push({
        type: 'wasteImpact',
        impact: trip.tripImpact!.wasteImpact,
      });
    return {
      totalImpact: trip.tripImpact!.totalImpact,
      detail,
    };
  }

  getOverviewPerTraveller(trip: Trip) {
    const detail: {
      type: string;
      impact: number;
      subcategories?: { type: string; impact: number }[];
    }[] = [];
    if (trip.tripImpact!.transportImpactDetail.length > 0) {
      let mainTransportImpact = 0;
      let onSiteTransportImpact = 0;
      const onSiteTransports: { type: string; impact: number }[] = [];

      trip.transports?.forEach((transport) => {
        if (transport.isMainTransport) {
          mainTransportImpact += transport.transportImpactDetail?.impact ?? 0;
        } else if (transport.transportImpactDetail?.impact) {
          onSiteTransportImpact += transport.transportImpactDetail.impact;
          onSiteTransports.push({
            type: this.getTransportSubtypeOrType(transport),
            impact: transport.transportImpactDetail.impact / trip.travellers!,
          });
        }
      });

      mainTransportImpact > 0 &&
        detail.push({
          type: 'mainTransportImpact',
          impact: mainTransportImpact / trip.travellers!,
        });

      onSiteTransportImpact > 0 &&
        detail.push({
          type: 'onSiteTransportImpact',
          impact: onSiteTransportImpact / trip.travellers!,
          subcategories: onSiteTransports,
        });
    }

    trip.tripImpact!.accommodationImpactDetail.length > 0 &&
      detail.push({
        type: 'accommodationPerTraveller',
        impact: trip.tripImpact!.accommodationPerTraveller,
      });
    trip.tripImpact!.activityImpactDetail.length > 0 &&
      detail.push({
        type: 'activityPerTraveller',
        impact: trip.tripImpact!.activityPerTraveller,
      });
    trip.tripImpact!.foodImpactDetail.length > 0 &&
      detail.push({
        type: 'foodPerTraveller',
        impact: trip.tripImpact!.foodPerTraveller,
      });
    trip.tripImpact!.wastePerTraveller > 0 &&
      detail.push({
        type: 'wastePerTraveller',
        impact: trip.tripImpact!.wastePerTraveller,
      });
    const order = [
      'mainTransportImpact',
      'onSiteTransportImpact',
      'accommodationPerTraveller',
      'activityPerTraveller',
      'foodPerTraveller',
      'wastePerTraveller',
    ];
    detail.sort((a, b) => {
      const ia = order.indexOf(a.type);
      const ib = order.indexOf(b.type);
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
    return {
      totalImpact: trip.tripImpact!.impactPerTraveller,
      detail,
    };
  }

  private getTransportSubtypeOrType(transport: Transport): string {
    return transport.type;
  }

  buildCustomPieChartData(
    totalImpact: number,
    detail: { type: string; impact: number }[],
    type: PieChartType,
  ): CustomPieChartData {
    let processedDetail = detail;

    if (type !== 'overview') {
      const grouped: { [type: string]: number } = {};
      detail.forEach((item) => {
        grouped[item.type] = (grouped[item.type] || 0) + item.impact;
      });
      processedDetail = Object.entries(grouped).map(([type, impact]) => ({
        type,
        impact,
      }));
    }

    const chartData: ChartData = {
      labels: processedDetail.map((item) => ({ ...item })),
      datasets: [
        {
          data: processedDetail.map((item) => this.round(item.impact)),
          backgroundColor: this.colorsService.getColors(processedDetail, type),
        },
      ],
    };

    if (totalImpact === 0) {
      chartData.datasets[0].data.push(1);
      chartData.datasets[0].backgroundColor = [
        this.colorsService.getEmptyColor(),
      ];
    }

    return { total: totalImpact, chartData };
  }

  async buildImpactPerDayChartData(trip: Trip): Promise<ChartData> {
    const chartData: ChartData = {
      labels: await this.getTranslatedDayList(trip.days ?? 0),
      datasets: [],
    };
    const transportDataset = {
      label: 'transport',
      data: Array(trip.days).fill(0),
      backgroundColor: this.colorsService.TRANSPORT_COLOR,
    };
    trip.transports?.forEach((transport) => {
      transport.scheduledDays?.forEach((day: number) => {
        const impact = transport.transportImpactDetail?.impact ?? 0;
        const days = transport.scheduledDays?.length ?? 1;
        const travellers = trip.travellers ?? 1;

        const dailyImpactPerTraveller = impact / days / travellers;
        transportDataset.data[day - 1] += dailyImpactPerTraveller;
      });
    });
    chartData.datasets.push(transportDataset);

    const accommodationDataset = {
      label: 'accommodation',
      data: Array(trip.days).fill(0),
      backgroundColor: this.colorsService.ACCOMMODATION_COLOR,
    };
    trip.accommodations?.forEach((accommodation) => {
      accommodation.scheduledDays?.forEach((day: number) => {
        const impact = accommodation.accommodationImpactDetail?.impact ?? 0;
        const days = accommodation.scheduledDays?.length ?? 1;
        const travellers = trip.travellers ?? 1;
        const dailyImpactPerTraveller = impact / days / travellers;
        accommodationDataset.data[day - 1] += dailyImpactPerTraveller;
      });
    });
    chartData.datasets.push(accommodationDataset);

    const activityDataset = {
      label: 'activity',
      data: Array(trip.days).fill(0),
      backgroundColor: this.colorsService.ACTIVITY_COLOR,
    };
    trip.activities?.forEach((activity) => {
      activity.scheduledDays?.forEach((day: number) => {
        const impact = activity.activityImpactDetail?.impact ?? 0;
        const days = activity.scheduledDays?.length ?? 1;
        const travellers = trip.travellers ?? 1;

        const dailyImpactPerTraveller = impact / days / travellers;
        activityDataset.data[day - 1] += dailyImpactPerTraveller;
      });
    });
    chartData.datasets.push(activityDataset);

    const mealDataset = {
      label: 'meal',
      data: Array(trip.days).fill(
        (trip.tripImpact?.foodPerTraveller ?? 0) / (trip.days ?? 1),
      ),
      backgroundColor: this.colorsService.FOOD_COLOR,
    };
    chartData.datasets.push(mealDataset);

    const wasteDataset = {
      label: 'waste',
      data: Array(trip.days).fill(
        (trip.tripImpact?.wastePerTraveller ?? 0) / (trip.days ?? 1),
      ),
      backgroundColor: this.colorsService.WASTE_COLOR,
    };
    chartData.datasets.push(wasteDataset);
    return chartData;
  }

  async getTranslatedDayList(numberOfDays: number) {
    const translatedDay = await firstValueFrom(
      this.translocoService.selectTranslate('travelBoards.day'),
    );
    const labels = [];
    for (let i = 1; i <= numberOfDays; i++) {
      labels.push(`${translatedDay} ${i}`);
    }
    return labels;
  }

  getEquivalentCards(tripImpact: TripImpact) {
    return [
      {
        data: tripImpact.equivalentBottledWater,
        label: 'impactReport.equivalent.water',
        icon: './assets/icons/bottles_icon.svg',
      },
      {
        data: tripImpact.equivalentTshirts,
        label: 'impactReport.equivalent.t-shirt',
        icon: './assets/icons/tshirt_icon.svg',
      },
      {
        data: tripImpact.equivalentVegetarianMeals,
        label: 'impactReport.equivalent.vegetarianMeal',
        icon: './assets/icons/vegetarian_icon.svg',
      },
      {
        data: tripImpact.equivalentBeefMeals,
        label: 'impactReport.equivalent.beefMeal',
        icon: './assets/icons/steak_icon.svg',
      },
    ];
  }

  private getLabel(detail: { type: string; impact: number }): object {
    return { type: detail.type, impact: this.round(detail.impact) };
  }

  // UTILITY FUNCTIONS
  round(number: number) {
    return Math.round(number * 10) / 10;
  }
  getReductionTips(tripId: string): Observable<TripReductionTipsResponse> {
    return this.http.get<TripReductionTipsResponse>(
      `${environment.apiUrl}/trips/${tripId}/reduction-tips`,
    );
  }

  private getCountryByLang(country: Country): string {
    const activeLang = this.translocoService.getActiveLang();
    switch (activeLang) {
      case 'fr':
        return country.FR;
      case 'en':
        return country.EN;
      case 'nl':
        return country.NL;
      default:
        return country.EN;
    }
  }
}
